import { captureCreate } from './capture';
import { draftFromWords, saveDraft } from './draft';
import { frontDoorOn } from './flag';
import { parseStartMessage } from './handoff';
import { SITE } from './landingPage';
import { takeSiteFragment } from './siteFragment';

const PREVIEW = /^https:\/\/fourklabs-ai--[a-z0-9-]+\.web\.app$/;

/**
 * Whether the words came from the site's own box. A link to /start#fd=… can be written by anyone, and a draft that
 * starts by itself spends the visitor's credits on the link's words. Exact origin: play.4klabs.ai hosts customer content.
 */
export function trustedReferrer(referrer: string, host: string): boolean {
  let origin: string;
  try {
    origin = new URL(referrer).origin;
  } catch {
    return false;
  }
  return origin === SITE || (host === 'beta.4klabs.ai' && PREVIEW.test(origin));
}

const parse = (raw: string): unknown => {
  try {
    return JSON.parse(decodeURIComponent(raw));
  } catch {
    return null;
  }
};

interface Arrival {
  raw: string | null;
  referrer: string;
  host: string;
  path: string;
  now?: number;
}

/**
 * The draft for words sent from 4klabs.ai, by the same steps as the landing page's own box. From the site it starts by
 * itself after sign-in; from anywhere else it waits for the person to press Start, and nothing is recorded.
 */
export function adoptSiteDraft({ raw, referrer, host, path, now = Date.now() }: Arrival): void {
  if (!raw || path !== '/start' || !frontDoorOn()) return;
  const data = parse(raw);
  if (!data || typeof data !== 'object') return;
  const start = parseStartMessage({ ...data, type: '4k:start' });
  if (!start) return;
  const draft = draftFromWords(start.prompt, start.chip, now);
  if (!trustedReferrer(referrer, host)) return saveDraft({ ...draft, armedAt: null, askFirst: true, noticed: false }, now);
  if (start.notice) captureCreate(draft, start.picked);
  saveDraft({ ...draft, noticed: start.notice }, now);
}

/** Called once from main.tsx, before the first render, so /start finds the draft. */
export const adoptSiteDraftFromUrl = (): void =>
  adoptSiteDraft({ raw: takeSiteFragment(), referrer: document.referrer, host: window.location.hostname, path: window.location.pathname });
